import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatError,
  MatFormField,
  MatHint,
  MatLabel
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { lastValueFrom, map } from 'rxjs';
import { AuthenticationService } from 'src/app/shared';
import { ResetPasswordPayload } from 'src/app/shared/services/authentication.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    MatFormField,
    MatInput,
    MatLabel,
    MatButton,
    RouterLink,
    ReactiveFormsModule,
    MatLabel,
    MatHint,
    MatError
  ],
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AuthenticationService]
})
export class ResetPasswordComponent {
  form = new FormGroup(
    {
      password: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(8)
      ]),
      password_confirmation: new FormControl<string>('', [Validators.required])
    },
    { validators: this.confirmValidator() }
  );

  authenticationService = inject(AuthenticationService);

  submitted = signal(false);

  mutation = injectMutation((_) => ({
    mutationFn: (payload: ResetPasswordPayload) =>
      lastValueFrom(this.authenticationService.resetPassword(payload))
  }));

  activatedRoute = inject(ActivatedRoute);

  params = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((params) => ({
        email: params.get('email') || '',
        token: params.get('token') || ''
      }))
    ),
    { initialValue: { email: '', token: '' } }
  );

  paramsError = computed(() => {
    const { email, token } = this.params();
    return !email || !token;
  });

  onSubmit() {
    if (this.form.valid) {
      const payload = { ...this.form.getRawValue(), ...this.params() };
      this.mutation.mutate(payload);
    }
  }

  confirmValidator(): ValidatorFn {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls['password'];
      const matchingControl = formGroup.controls['password_confirmation'];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmedValidator
      ) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmedValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
      return null;
    };
  }
}
